import { createFileRoute, useNavigate } from '@tanstack/solid-router';
import { Show, createEffect, createSignal, onMount } from 'solid-js';

import Header from '~/components/layout/Header';
import DrawingControls, {
    type DrawingMode
} from '~/components/map/DrawingControls';
import GoogleMap from '~/components/map/GoogleMap';
import MapDrawingTutorial from '~/components/map/MapDrawingTutorial';
import MapToolbarButton from '~/components/map/MapToolbarButton';
import ModeToggle, { type MapMode } from '~/components/map/ModeToggle';
import PolygonDrawer from '~/components/map/PolygonDrawer';
import QuickDrawOverlay from '~/components/map/QuickDrawOverlay';
import { getQuestions, getSlug } from '~/lib/config/questions';
import type { COUNTRY_CODE } from '~/lib/graphql/types';
import { BUILDING_TYPE, type PolygonInput } from '~/lib/graphql/types';
import { trackEvent, trackSelectedArea } from '~/lib/helpers/analytics';
import { posthog } from '~/lib/helpers/posthog';
import { countryCode, locale } from '~/lib/i18n';
import { getNearbyCountFn } from '~/lib/server/social-proof';
import { useCalculator } from '~/lib/store';
import type { PolygonPoint } from '~/lib/store/types';

export const Route = createFileRoute('/$locale/map')({
    component: MapPage
});

type ToolMode = 'draw' | 'move';

const MANUAL_MODE_FEATURE_FLAG = 'calculator-map-manual-mode';
const MAP_TUTORIAL_DISMISSED_KEY = 'calculator-map-drawing-tutorial-dismissed';

function MapPage() {
    const [state, actions] = useCalculator();
    const navigate = useNavigate();

    const [mapInstance, setMapInstance] = createSignal<google.maps.Map | null>(
        null
    );
    const [roofArea, setRoofArea] = createSignal<number | null>(
        state.quote.area ? Number(state.quote.area) : null
    );
    const [polygon, setPolygon] = createSignal<PolygonPoint[]>(
        state.quote.polygon ?? []
    );
    const [drawingMode, setDrawingMode] = createSignal<DrawingMode>('draw');
    const [mapMode, setMapMode] = createSignal<MapMode>('quickdraw');
    const [toolMode, setToolMode] = createSignal<ToolMode>('draw');
    const [manualModeEnabled, setManualModeEnabled] = createSignal(false);
    const [showTutorial, setShowTutorial] = createSignal(false);
    const [errorMsg, setErrorMsg] = createSignal<string | null>(null);
    const [mapsReady, setMapsReady] = createSignal(false);
    const [aiPolygon, setAiPolygon] = createSignal<google.maps.Polygon | null>(
        null
    );
    const [nearbyCount, setNearbyCount] = createSignal<number | null>(null);
    const [showBanner, setShowBanner] = createSignal(false);
    const [rotateHandle, setRotateHandle] =
        createSignal<google.maps.Marker | null>(null);
    const [vertexMarkers, setVertexMarkers] = createSignal<
        google.maps.Marker[]
    >([]);

    onMount(() => {
        posthog.capture('$pageview');

        if (!state.quote.address || !state.quote.type) {
            navigate({ to: '/$locale', search: (prev) => prev });
            return;
        }

        const syncManualModeFlag = () => {
            try {
                const enabled = posthog.isFeatureEnabled(
                    MANUAL_MODE_FEATURE_FLAG
                );
                if (enabled !== undefined) {
                    setManualModeEnabled(Boolean(enabled));
                }
            } catch {
                // Leave the current default when feature flags are unavailable.
            }
        };

        syncManualModeFlag();
        posthog.onFeatureFlags(() => {
            syncManualModeFlag();
        });

        if (
            window.localStorage.getItem(MAP_TUTORIAL_DISMISSED_KEY) !== 'true'
        ) {
            setShowTutorial(true);
            posthog.capture('map_drawing_tutorial_shown');
        }

        function check() {
            if (typeof google !== 'undefined' && google.maps?.Map) {
                setMapsReady(true);
            } else {
                setTimeout(check, 200);
            }
        }
        check();

        // Fetch nearby customer count for social proof banner
        const userLat = Number(state.quote.lat);
        const userLng = Number(state.quote.lng);

        if (!isNaN(userLat) && !isNaN(userLng)) {
            getNearbyCountFn({
                data: {
                    countryCode: countryCode() as COUNTRY_CODE,
                    lat: userLat,
                    lng: userLng
                }
            })
                .then((result) => {
                    if (result.success) {
                        setNearbyCount(result.count);
                        setShowBanner(true);
                    }
                })
                .catch(() => {
                    // Fail silently — banner stays hidden
                });
        }
    });

    createEffect(() => {
        if (!manualModeEnabled() && mapMode() === 'manual') {
            handleModeChange('quickdraw');
        }
    });

    function handleAreaChange(area: number, poly: PolygonInput[]) {
        setRoofArea(area);
        setPolygon(poly);
    }

    function handleQuickDrawResult(poly: PolygonPoint[], area: number) {
        if (aiPolygon()) {
            aiPolygon()!.setMap(null);
            setAiPolygon(null);
        }
        if (rotateHandle()) {
            rotateHandle()!.setMap(null);
            setRotateHandle(null);
        }
        vertexMarkers().forEach((m) => m.setMap(null));
        setVertexMarkers([]);

        setRoofArea(Math.round(area));
        setPolygon(poly);
        setErrorMsg(null);

        const map = mapInstance();
        if (map && poly.length > 0) {
            const gPolygon = new google.maps.Polygon({
                paths: poly,
                strokeColor: '#FFC300',
                strokeWeight: 3,
                fillColor: '#FFCC15',
                fillOpacity: 0.35,
                editable: false,
                draggable: true,
                map
            });

            const recalcArea = () => {
                const path = gPolygon.getPath();
                const newArea = Math.round(
                    google.maps.geometry.spherical.computeArea(path)
                );
                const newPoly = path.getArray().map((p) => ({
                    lat: p.lat(),
                    lng: p.lng()
                }));
                setRoofArea(newArea);
                setPolygon(newPoly);
            };
            google.maps.event.addListener(
                gPolygon.getPath(),
                'set_at',
                recalcArea
            );
            google.maps.event.addListener(
                gPolygon.getPath(),
                'insert_at',
                recalcArea
            );

            // Rotation handle — a draggable marker above the polygon that
            // rotates every vertex around the centroid as the user drags.
            const rotateIconSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='36' height='36' viewBox='0 0 24 24'>
<circle cx='12' cy='12' r='11' fill='white' stroke='#3657DA' stroke-width='1.5'/>
<path fill='none' stroke='#3657DA' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M4.5 12a7.5 7.5 0 0 1 13-5.2'/>
<path fill='#3657DA' d='M17.2 4.5l1.3 3.4-3.5-.8z'/>
<path fill='none' stroke='#3657DA' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M19.5 12a7.5 7.5 0 0 1-13 5.2'/>
<path fill='#3657DA' d='M6.8 19.5l-1.3-3.4 3.5.8z'/>
</svg>`;
            const handleIcon: google.maps.Icon = {
                url:
                    'data:image/svg+xml;charset=UTF-8,' +
                    encodeURIComponent(rotateIconSvg),
                scaledSize: new google.maps.Size(36, 36),
                anchor: new google.maps.Point(18, 18)
            };

            const computeCentroid = (): google.maps.LatLng => {
                const bounds = new google.maps.LatLngBounds();
                gPolygon.getPath().forEach((pt) => bounds.extend(pt));
                return bounds.getCenter();
            };

            // The handle's position is expressed as an offset from the polygon
            // centroid (heading + distance). On any polygon change we reposition
            // the handle using this offset, so it stays anchored to the same
            // logical point on the polygon — it follows translation, rotates
            // with rotation, and never drifts with the user's finger.
            const initialBounds = new google.maps.LatLngBounds();
            gPolygon.getPath().forEach((pt) => initialBounds.extend(pt));
            const initialCentroid = initialBounds.getCenter();
            const initialAnchor = new google.maps.LatLng(
                initialBounds.getNorthEast().lat() +
                    (initialBounds.getNorthEast().lat() -
                        initialBounds.getSouthWest().lat()) *
                        0.25,
                initialCentroid.lng()
            );
            const handleOffset = {
                heading: google.maps.geometry.spherical.computeHeading(
                    initialCentroid,
                    initialAnchor
                ),
                distance: google.maps.geometry.spherical.computeDistanceBetween(
                    initialCentroid,
                    initialAnchor
                )
            };

            const handle = new google.maps.Marker({
                position: initialAnchor,
                map,
                draggable: true,
                icon: handleIcon,
                cursor: 'grab',
                title: 'Rotate',
                zIndex: 1000
            });

            const anchorHandle = () => {
                const c = computeCentroid();
                handle.setPosition(
                    google.maps.geometry.spherical.computeOffset(
                        c,
                        handleOffset.distance,
                        handleOffset.heading
                    )
                );
            };

            let rotateStart: {
                centroid: google.maps.LatLng;
                initialFingerAngle: number;
                initialHandleHeading: number;
                initialVertices: Array<{ heading: number; distance: number }>;
            } | null = null;

            handle.addListener('dragstart', () => {
                const centroid = computeCentroid();
                const pos = handle.getPosition();
                if (!pos) return;
                const initialFingerAngle =
                    google.maps.geometry.spherical.computeHeading(
                        centroid,
                        pos
                    );
                const initialVertices = gPolygon
                    .getPath()
                    .getArray()
                    .map((v) => ({
                        heading: google.maps.geometry.spherical.computeHeading(
                            centroid,
                            v
                        ),
                        distance:
                            google.maps.geometry.spherical.computeDistanceBetween(
                                centroid,
                                v
                            )
                    }));
                rotateStart = {
                    centroid,
                    initialFingerAngle,
                    initialHandleHeading: handleOffset.heading,
                    initialVertices
                };
            });

            handle.addListener('drag', (e: google.maps.MapMouseEvent) => {
                if (!rotateStart || !e.latLng) return;
                // Finger's angle around the (stable) centroid drives rotation.
                const newFingerAngle =
                    google.maps.geometry.spherical.computeHeading(
                        rotateStart.centroid,
                        e.latLng
                    );
                const delta = newFingerAngle - rotateStart.initialFingerAngle;
                const rotated = rotateStart.initialVertices.map((v) =>
                    google.maps.geometry.spherical.computeOffset(
                        rotateStart!.centroid,
                        v.distance,
                        v.heading + delta
                    )
                );
                // Update path in place so listeners attached to the MVCArray
                // (recalcArea, anchorHandle on set_at) survive the rotation.
                const path = gPolygon.getPath();
                rotated.forEach((pt, i) => path.setAt(i, pt));
                // Anchor the handle — it rotates with the polygon but stays
                // glued to its canonical offset, not the user's finger.
                handleOffset.heading = rotateStart.initialHandleHeading + delta;
                anchorHandle();
                syncVertexMarkers();
            });

            handle.addListener('dragend', () => {
                rotateStart = null;
                recalcArea();
                anchorHandle();
                syncVertexMarkers();
            });

            // Vertex markers — custom large draggable dots on every vertex so
            // touch users can easily see and grab individual corners.
            const vertexIcon: google.maps.Symbol = {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 9,
                fillColor: '#FFFFFF',
                fillOpacity: 1,
                strokeColor: '#3657DA',
                strokeWeight: 2.5
            };
            const markers: google.maps.Marker[] = [];
            gPolygon
                .getPath()
                .getArray()
                .forEach((pt, idx) => {
                    const m = new google.maps.Marker({
                        position: pt,
                        map,
                        draggable: true,
                        cursor: 'grab',
                        icon: vertexIcon,
                        zIndex: 900
                    });
                    m.addListener('drag', () => {
                        const pos = m.getPosition();
                        if (pos) gPolygon.getPath().setAt(idx, pos);
                    });
                    m.addListener('dragend', () => {
                        recalcArea();
                        anchorHandle();
                    });
                    markers.push(m);
                });

            const syncVertexMarkers = () => {
                gPolygon.getPath().forEach((pt, idx) => {
                    if (markers[idx]) markers[idx].setPosition(pt);
                });
            };

            google.maps.event.addListener(gPolygon, 'dragend', () => {
                recalcArea();
                anchorHandle();
                syncVertexMarkers();
            });
            google.maps.event.addListener(gPolygon.getPath(), 'set_at', () => {
                anchorHandle();
                syncVertexMarkers();
            });
            google.maps.event.addListener(
                gPolygon.getPath(),
                'insert_at',
                () => {
                    anchorHandle();
                    syncVertexMarkers();
                }
            );

            setAiPolygon(gPolygon);
            setRotateHandle(handle);
            setVertexMarkers(markers);
            handleToolChange('move');
        }
    }

    function handleQuickDrawError(message: string) {
        setErrorMsg(message);
    }

    function handleReset() {
        setRoofArea(null);
        setPolygon([]);
        setErrorMsg(null);
        if (aiPolygon()) {
            aiPolygon()!.setMap(null);
            setAiPolygon(null);
        }
        if (rotateHandle()) {
            rotateHandle()!.setMap(null);
            setRotateHandle(null);
        }
        vertexMarkers().forEach((m) => m.setMap(null));
        setVertexMarkers([]);
        setDrawingMode('draw');
        setToolMode('draw');
    }

    function handleEdit() {
        const poly = aiPolygon();
        if (!poly) return;
        poly.setEditable(true);
        setToolMode('move');
    }

    function handleDelete() {
        handleReset();
    }

    function handleModeChange(mode: MapMode) {
        if (mode === 'manual' && !manualModeEnabled()) return;

        handleReset();
        const map = mapInstance();
        if (map) {
            if (mode === 'quickdraw') {
                handleToolChange('draw', map);
            } else {
                map.setOptions({
                    gestureHandling: 'greedy',
                    draggableCursor: 'crosshair'
                });
            }
        }
        setMapMode(mode);
    }

    function handleToolChange(tool: ToolMode, mapOverride?: google.maps.Map) {
        setToolMode(tool);
        const map = mapOverride ?? mapInstance();
        if (map) {
            map.setOptions({
                gestureHandling: tool === 'move' ? 'greedy' : 'none',
                draggableCursor: tool === 'draw' ? 'crosshair' : 'grab'
            });
        }
        const poly = aiPolygon();
        if (poly) {
            if (tool === 'draw') {
                // Stop editing and let the overlay capture redraw clicks
                poly.setEditable(false);
                poly.setDraggable(false);
            } else {
                poly.setDraggable(true);
            }
        }
        const handle = rotateHandle();
        if (handle) {
            handle.setVisible(tool !== 'draw');
        }
        vertexMarkers().forEach((m) => m.setVisible(tool !== 'draw'));
    }

    function handleMapReady(map: google.maps.Map) {
        setMapInstance(map);
        if (mapMode() === 'quickdraw') {
            handleToolChange(toolMode(), map);
        } else {
            map.setOptions({
                gestureHandling: 'greedy',
                draggableCursor: 'crosshair'
            });
        }

        // Pin at address location so user knows which roof to draw around
        new google.maps.Marker({
            position: { lat: lat(), lng: lng() },
            map,
            zIndex: 5
        });
    }

    function handleCloseTutorial() {
        setShowTutorial(false);
        window.localStorage.setItem(MAP_TUTORIAL_DISMISSED_KEY, 'true');
        posthog.capture('map_drawing_tutorial_dismissed');
    }

    function handleBack() {
        trackEvent('Step: map', {
            quote: state.quote,
            set: state.set,
            direction: 'back',
            utm: state.utm
        });
        const buildingType = state.quote.type ?? BUILDING_TYPE.LANDED_HOUSE;
        const questions = getQuestions(countryCode(), buildingType);

        if (questions.length === 0) {
            console.error(
                'Cannot determine previous page: no questions found.'
            );
            navigate({ to: '/$locale', search: (prev) => prev });
            return;
        }

        const lastSlug = getSlug(questions[questions.length - 1]);
        navigate({
            to: '/$locale/question/$slug',
            params: { slug: lastSlug },
            search: (prev) => prev
        });
    }

    function handleContinue() {
        const area = roofArea();
        const poly = polygon();

        if (!area || area <= 0) {
            setErrorMsg(
                'Please draw your roof area on the map, or enter it manually below.'
            );
            return;
        }

        setErrorMsg(null);
        actions.setQuote('area', String(area));
        actions.setQuote(
            'polygon',
            poly.length > 0 ? poly : [{ lat: 0, lng: 0 }]
        );

        trackEvent('Step: map', {
            quote: state.quote,
            set: state.set,
            direction: 'continue',
            utm: state.utm
        });

        trackSelectedArea(state.quote.type ?? null);
        navigate({ to: '/$locale/contact', search: (prev) => prev });
    }

    const lat = () => Number(state.quote.lat) || 1.3521;
    const lng = () => Number(state.quote.lng) || 103.8198;

    return (
        <div class="h-screen flex flex-col lg:grid lg:grid-cols-12">
            {/* Header (mobile only) */}
            <div class="lg:hidden">
                <Header />
            </div>

            {/* Sidebar (desktop only) */}
            <div class="hidden lg:flex lg:col-span-3 flex-col bg-brand-blue text-white p-6 overflow-y-auto">
                {/* Branding */}
                <div class="flex justify-between items-center mb-8">
                    <a href="/">
                        <img
                            src="/getsolar-horizontal-white-yellow.png"
                            alt="GetSolar"
                            width={160}
                            height={32}
                        />
                    </a>
                    <div class="flex items-center gap-3">
                        <a
                            href={`mailto:${locale().support.email}?subject=Request For Solar Quote From GetSolar`}>
                            <img
                                src="/email_white.svg"
                                alt="Email"
                                width={20}
                                height={20}
                            />
                        </a>
                        <a href={`tel:${locale().support.number}`}>
                            <img
                                src="/phone_white.svg"
                                alt="Phone"
                                width={20}
                                height={20}
                            />
                        </a>
                    </div>
                </div>

                <div class="flex items-baseline justify-between border-b border-white/20 pb-3 mb-5">
                    <span class="text-sm font-medium text-white/80">
                        Roof Area
                    </span>
                    <span class="text-lg font-semibold text-white">
                        {roofArea() ? `${roofArea()} m²` : '— m²'}
                    </span>
                </div>

                <div class="mb-4">
                    <label
                        class="block text-sm font-medium text-white mb-2"
                        for="roof-area-sidebar">
                        Or enter your roof size
                    </label>
                    <input
                        id="roof-area-sidebar"
                        type="number"
                        min="1"
                        placeholder="e.g. 50"
                        value={roofArea() ?? ''}
                        onInput={(e) => {
                            const val = Number(e.currentTarget.value);
                            setRoofArea(val > 0 ? val : null);
                        }}
                        class="w-full px-4 py-3 bg-white border border-white/20 rounded-lg text-sm text-brand-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                    />
                </div>

                <div class="flex items-start gap-3 rounded-lg bg-white/10 p-3 mb-4">
                    <svg
                        class="w-5 h-5 shrink-0 text-brand-yellow mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2">
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                        />
                    </svg>
                    <div>
                        <p class="text-xs font-semibold text-white">Tip</p>
                        <p class="text-xs leading-5 text-white/80 mt-0.5">
                            If you are unsure about your roof size, you can
                            enter your ground floor area.
                        </p>
                    </div>
                </div>

                <Show when={errorMsg()}>
                    <p class="text-sm text-red-300 mb-3">{errorMsg()}</p>
                </Show>

                <div class="mt-auto flex items-center gap-3">
                    <button
                        type="button"
                        onClick={handleBack}
                        class="rounded-full border border-white/60 bg-transparent px-8 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors">
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={handleContinue}
                        class="flex-1 rounded-full bg-brand-yellow px-8 py-3 text-sm font-semibold text-brand-black hover:opacity-90 transition-opacity">
                        Continue
                    </button>
                </div>
            </div>

            {/* Map (col-span-9 on desktop, flex-1 on mobile) */}
            <div class="flex-1 lg:col-span-9 relative min-h-0">
                <Show
                    when={mapsReady()}
                    fallback={
                        <div class="w-full h-full bg-gray-200 flex items-center justify-center">
                            <div class="text-center text-gray-500">
                                <svg
                                    class="animate-spin h-8 w-8 mx-auto mb-3 text-brand-blue"
                                    fill="none"
                                    viewBox="0 0 24 24">
                                    <circle
                                        class="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        stroke-width="4"
                                    />
                                    <path
                                        class="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                    />
                                </svg>
                                <p class="text-sm">Loading map...</p>
                            </div>
                        </div>
                    }>
                    <div class="w-full h-full">
                        <GoogleMap
                            lat={lat()}
                            lng={lng()}
                            onMapReady={handleMapReady}
                        />
                    </div>

                    {/* Social proof banner (floating top of map) */}
                    <Show when={showBanner()}>
                        <div class="absolute top-3 left-3 right-3 lg:top-4 lg:left-6 lg:right-6 z-10 flex items-center gap-2.5 lg:gap-3 rounded-2xl border-2 border-brand-blue bg-white/90 backdrop-blur px-2.5 py-2.5 lg:px-3 lg:py-3 shadow-lg">
                            <div class="flex shrink-0 w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-brand-yellow items-center justify-center">
                                <svg
                                    class="w-4 h-4 lg:w-5 lg:h-5 text-brand-blue"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    stroke-width="2">
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                                    />
                                </svg>
                            </div>
                            <p class="flex-1 text-xs lg:text-sm font-medium text-gray-800 leading-snug">
                                <Show
                                    when={(nearbyCount() ?? 0) > 0}
                                    fallback="Be the first in your area to go solar!">
                                    {nearbyCount()} homeowner
                                    {nearbyCount() !== 1 ? 's' : ''} near you
                                    chose to get solar recently — don't miss
                                    out! Click{' '}
                                    <span class="font-bold">"Continue"</span> to
                                    find out more.
                                </Show>
                            </p>
                            <button
                                type="button"
                                onClick={() => setShowBanner(false)}
                                class="shrink-0 flex items-center justify-center w-6 h-6 rounded-full border-[1.5px] border-brand-blue text-brand-blue hover:bg-brand-blue/10 transition-colors"
                                aria-label="Dismiss">
                                <svg
                                    class="w-3 h-3"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    stroke-width="2.5">
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                    </Show>

                    {/* Quick Draw overlay */}
                    <Show when={mapMode() === 'quickdraw' && mapInstance()}>
                        <QuickDrawOverlay
                            map={mapInstance()!}
                            drawEnabled={toolMode() === 'draw'}
                            onResult={handleQuickDrawResult}
                            onError={handleQuickDrawError}
                        />
                    </Show>

                    {/* Manual mode */}
                    <Show when={mapMode() === 'manual'}>
                        <PolygonDrawer
                            map={mapInstance()}
                            mode={drawingMode()}
                            onAreaChange={handleAreaChange}
                            onReset={handleReset}
                        />
                    </Show>

                    {/* Mode toggle (top center) */}
                    <Show when={manualModeEnabled()}>
                        <div class="absolute top-4 left-1/2 -translate-x-1/2 z-10">
                            <ModeToggle
                                mode={mapMode()}
                                onModeChange={handleModeChange}
                                manualEnabled={manualModeEnabled()}
                            />
                        </div>
                    </Show>

                    {/* Manual mode drawing controls */}
                    <Show when={mapMode() === 'manual'}>
                        <div class="absolute top-16 left-1/2 -translate-x-1/2 z-10">
                            <DrawingControls
                                activeMode={drawingMode()}
                                onModeChange={setDrawingMode}
                            />
                        </div>
                    </Show>

                    {/* Floating toolbar (right side) */}
                    <Show when={mapMode() === 'quickdraw'}>
                        <div
                            class="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-1 md:gap-1.5 bg-white rounded-lg border border-gray-200/60 p-1 md:p-1.5"
                            style={{
                                'box-shadow':
                                    '0 4px 24px rgba(30, 75, 149, 0.12)'
                            }}>
                            <MapToolbarButton
                                label="Move"
                                onClick={() => handleToolChange('move')}
                                active={toolMode() === 'move'}
                                icon={
                                    <svg
                                        class="w-5 h-5 md:w-6 md:h-6"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        stroke-width="2">
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                                        />
                                    </svg>
                                }
                            />

                            <MapToolbarButton
                                label="Draw"
                                onClick={() => handleToolChange('draw')}
                                active={toolMode() === 'draw'}
                                icon={
                                    <svg
                                        class="w-5 h-5 md:w-6 md:h-6"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        stroke-width="2">
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                        />
                                    </svg>
                                }
                            />

                            <div class="border-t border-gray-200 my-0.5" />

                            <MapToolbarButton
                                label="Edit"
                                onClick={handleEdit}
                                title="Edit vertices"
                                disabled={!roofArea()}
                                icon={
                                    <svg
                                        class="w-5 h-5 md:w-6 md:h-6"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        stroke-width="2">
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                        />
                                    </svg>
                                }
                            />

                            <MapToolbarButton
                                label="Delete"
                                onClick={handleDelete}
                                tone="danger"
                                disabled={!roofArea()}
                                icon={
                                    <svg
                                        class="w-5 h-5 md:w-6 md:h-6"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        stroke-width="2">
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                    </svg>
                                }
                            />
                        </div>
                    </Show>

                    {/* Instruction overlay for manual mode */}
                    <Show when={mapMode() === 'manual' && !roofArea()}>
                        <div class="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-black/70 text-white text-sm px-4 py-2 rounded-full whitespace-nowrap">
                            Click on the map to draw your roof outline
                        </div>
                    </Show>

                    <MapDrawingTutorial
                        open={showTutorial()}
                        onClose={handleCloseTutorial}
                    />
                </Show>
            </div>

            {/* Bottom panel (mobile only) */}
            <div class="lg:hidden shrink-0 border-t border-gray-200 bg-white px-6 py-4">
                <div class="max-w-xl mx-auto">
                    <div class="flex items-baseline justify-between border-b border-gray-200 pb-2.5 mb-4">
                        <span class="text-sm font-medium text-gray-700">
                            Roof Area
                        </span>
                        <span class="text-lg font-semibold text-brand-black">
                            {roofArea() ? `${roofArea()} m²` : '— m²'}
                        </span>
                    </div>

                    <div class="mb-3">
                        <label
                            class="block text-sm font-medium text-brand-black mb-2"
                            for="roof-area-mobile">
                            Or enter your roof size
                        </label>
                        <input
                            id="roof-area-mobile"
                            type="number"
                            min="1"
                            placeholder="e.g. 50"
                            value={roofArea() ?? ''}
                            onInput={(e) => {
                                const val = Number(e.currentTarget.value);
                                setRoofArea(val > 0 ? val : null);
                            }}
                            class="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                        />
                    </div>

                    <div class="flex items-start gap-3 rounded-lg bg-brand-blue/5 p-3 mb-3">
                        <svg
                            class="w-5 h-5 shrink-0 text-brand-blue mt-0.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            stroke-width="2">
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                            />
                        </svg>
                        <div>
                            <p class="text-xs font-semibold text-brand-blue">
                                Tip
                            </p>
                            <p class="text-xs leading-5 text-gray-600 mt-0.5">
                                If you are unsure about your roof size, you can
                                enter your ground floor area.
                            </p>
                        </div>
                    </div>

                    <Show when={errorMsg()}>
                        <p class="text-sm text-red-600 mb-3">{errorMsg()}</p>
                    </Show>

                    <div class="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={handleBack}
                            class="rounded-full border border-brand-blue bg-white px-8 py-3 text-sm font-semibold text-brand-blue hover:bg-brand-blue/5 transition-colors">
                            Back
                        </button>
                        <button
                            type="button"
                            onClick={handleContinue}
                            class="flex-1 rounded-full bg-brand-yellow px-8 py-3 text-sm font-semibold text-brand-black hover:opacity-90 transition-opacity">
                            Continue
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
